// ** React Imports
import React, { useEffect, useState, Dispatch, SetStateAction } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import { Button, FormControl, Typography } from '@mui/material'

// ** Child Imports
import CouponCardPreview from './CouponCardPreview'
import InputTextField from '../../common/form-elements/InputTextField'
import BulletedTextEditor from './BulletedTextEditor'
import FloGenericDialog from '../../common/feedback-and-dialog/GenericDialog'

// ** Type Imports
import { DiscountEditorDataType } from 'src/lib/types/discounts'

interface DiscountDescriptionEditorProps {
  isDialog?: boolean
  isOpen?: boolean
  editorData: DiscountEditorDataType
  handleClose?: () => void
  handleSave: (descTitle: string, desc: string, longDesc: string) => void
  saveTriggered?: boolean
}

const DiscountDescriptionEditor: React.FC<DiscountDescriptionEditorProps> = ({
  isDialog = true,
  isOpen,
  editorData,
  handleClose,
  handleSave,
  saveTriggered
}) => {
  const { descriptionTitle, description, longDescription } = editorData || {}

  const [descTitle, setDescTitle] = useState<string>('')
  const [desc, setDesc] = useState<string>('')
  const [longDesc, setLongDesc] = useState<string[]>([])
  const [textAreaContent, setTextAreaContent] = useState<string>('')

  // For setting local states.
  useEffect(() => {
    if (typeof descriptionTitle === 'undefined') return
    setDescTitle(descriptionTitle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionTitle, isOpen])

  useEffect(() => {
    if (typeof description === 'undefined') return
    setDesc(description)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description, isOpen])

  useEffect(() => {
    if (typeof longDescription === 'undefined') return
    setLongDesc(longDescription)
    getTextAreaContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longDescription, isOpen])

  // To encounter change in the textarea content and saving that content into long description state.
  useEffect(() => {
    const modifiedLongDesc = textAreaContent?.replaceAll('\n', '')?.split('•  ')
    setLongDesc([...modifiedLongDesc?.slice(1, modifiedLongDesc.length)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textAreaContent])

  // Modifying the longDescription into textarea content type.
  const getTextAreaContent = () => {
    let tempContent = ''
    longDescription?.forEach((point: string, index: number) => {
      if (index === longDescription?.length - 1) tempContent += '•  ' + point
      else tempContent += '•  ' + point + '\n'
    })
    setTextAreaContent(tempContent)
  }

  useEffect(() => {
    if (!Boolean(saveTriggered)) return
    handleSaveClick()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTriggered])

  const handleSaveClick = async () => {
    handleSave(descTitle, desc, textAreaContent?.replaceAll('•  ', ''))
  }

  const drawerComponents = () => (
    <Box sx={{ width: '100%', display: 'flex', gap: 4, flexDirection: 'column' }}>
      <CouponCardPreview
        id={editorData?.couponId}
        identifier={editorData?.identifier}
        descTitle={descTitle}
        desc={desc}
        longDesc={longDesc}
      />
      <FormControl sx={{ width: '100%' }}>
        <div className='flex flex-col w-full gap-2 py-0'>
          <InputTextField
            label={'Title'}
            textFieldId='desc-title-input'
            inputPlaceholder={'Enter discount description title'}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: 60 }}
            isRequired={true}
            characterCounter={true}
            maxCharacter={60}
            inputValue={descTitle}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDescTitle(event.target.value)
            }}
          />
          <InputTextField
            label={'Description'}
            textFieldId='desc-input'
            inputPlaceholder={'Enter discount description'}
            inputType='text'
            inputMargin={'none'}
            isFullWidth={true}
            inputProps={{ maxLength: 100 }}
            isRequired={true}
            inputValue={desc}
            maxCharacter={100}
            characterCounter={true}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDesc(event.target.value)
            }}
          />
          <div className='flex flex-col gap-1'>
            <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
              Details
            </Typography>
            <BulletedTextEditor
              textAreaContent={textAreaContent}
              setTextAreaContent={(value: any) => {
                setTextAreaContent(value)
              }}
            />
          </div>
        </div>
      </FormControl>
    </Box>
  )

  return (
    <React.Fragment>
      {Boolean(isDialog) ? (
        <FloGenericDialog
          isOpen={Boolean(isOpen)}
          handleClose={() => typeof handleClose !== 'undefined' && handleClose()}
          handleJob={handleSaveClick}
          jobButtonText={'Save'}
          closeButtonText={'Cancel'}
          content={drawerComponents()}
          title={'Change description'}
        />
      ) : (
        drawerComponents()
      )}
    </React.Fragment>
  )
}

export default DiscountDescriptionEditor
